const API_BASE = process.env.REACT_APP_API_BASE || "";

// Build a clean, user-friendly error message from backend JSON errors
function formatApiError(json, status) {
  if (!json || typeof json !== "object") {
    return `Request failed (HTTP ${status}). Please try again.`;
  }

  // Our backend sends structures like:
  // { ok:false, error:"Invalid issuance jurisdiction", field:"issuanceJurisdiction", value:"Legoland", hint:"...", details:"..." }
  // { ok:false, error:"Invalid sale jurisdictions", field:"saleJurisdictions", invalid:[{value:"Scandinavia", reason:"..."}], hint:"..." }

  const error = typeof json.error === "string" ? json.error : "Invalid input";
  const field = typeof json.field === "string" ? json.field : "";
  const value = json.value;

  const fieldLabelMap = {
    issuanceJurisdiction: "issuance jurisdiction",
    saleJurisdictions: "sale jurisdictions",
  };

  const fieldLabel = fieldLabelMap[field] || field;

  // Sale jurisdictions: list invalid ones nicely
  if (field === "saleJurisdictions" && Array.isArray(json.invalid) && json.invalid.length) {
    const bad = json.invalid
      .map((x) => (x && x.value ? `"${String(x.value)}"` : null))
      .filter(Boolean)
      .join(", ");

    const hint =
      typeof json.hint === "string"
        ? json.hint
        : 'Use real country names (e.g., Germany, Spain, Netherlands) or recognized labels (EU/EEA/UK).';

    return `${error}: ${bad}. ${hint}`;
  }

  // Issuance jurisdiction (single value)
  if (field === "issuanceJurisdiction") {
    const shown = value ? `"${String(value)}"` : "";
    const hint =
      typeof json.hint === "string"
        ? json.hint
        : 'Use a real country name (e.g., Germany, France, Denmark, United States) or a recognized label (EU/EEA/UK).';

    return `${error}${shown ? `: ${shown}` : ""}. ${hint}`;
  }

  // Generic fallback (keep it short, avoid redundancy)
  if (fieldLabel) {
    const shown = value ? ` "${String(value)}"` : "";
    const hint = typeof json.hint === "string" ? ` ${json.hint}` : "";
    return `${error} (field: ${fieldLabel})${shown}.${hint}`.trim();
  }

  return `${error}.`;
}

// payload = object collected from the UI form
async function analyzeCase(payload, options = {}) {
  const control = {};
  if (Object.prototype.hasOwnProperty.call(options, "forceFresh")) control.forceFresh = Boolean(options.forceFresh);
  if (options.assessmentMode) control.assessmentMode = options.assessmentMode;
  if (options.scenarioId) control.scenarioId = options.scenarioId;
  if (options.baselineAssessmentId) control.baselineAssessmentId = options.baselineAssessmentId;

  const response = await fetch(`${API_BASE}/api/evaluate/v2`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
  ...payload,
  ...control,
  email: localStorage.getItem("tvac_access_email") || null,
}),
  });

  if (!response.ok) {
    // Try JSON first (our backend sends JSON on 400)
    let json = null;
    try {
      json = await response.json();
    } catch {
      // Fallback to text if not JSON
      const text = await response.text().catch(() => "");
      throw new Error(
        text
          ? `Request failed (HTTP ${response.status}): ${text.slice(0, 200)}`
          : `Request failed (HTTP ${response.status}).`
      );
    }

    throw new Error(formatApiError(json, response.status));
  }

  return await response.json();
}


// masterAssessment = canonical English assessment returned by /api/evaluate/v2
async function localizeReport(masterAssessment, reportLanguageCode = "en", options = {}) {
  const timeoutMs = Number(options.timeoutMs || 210000); // hard UX timeout for language generation
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  let response;
  try {
    response = await fetch(`${API_BASE}/api/localize/v2`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller?.signal,
      body: JSON.stringify({
        masterAssessment,
        reportLanguageCode,
        forceRefresh: Boolean(options.forceRefresh),
        reportInput: options.reportInput || null,
        email: localStorage.getItem("tvac_access_email") || null,
      }),
    });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("The selected report language took too long to prepare. Please retry the language version or use the English master report.");
    }
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let json = null;
    try {
      json = await response.json();
    } catch {
      const text = await response.text().catch(() => "");
      throw new Error(
        text
          ? `Request failed (HTTP ${response.status}): ${text.slice(0, 200)}`
          : `Request failed (HTTP ${response.status}).`
      );
    }

    throw new Error(formatApiError(json, response.status));
  }

  return await response.json();
}

async function downloadReportPdf(exportToken) {
  const token = String(exportToken || "").trim();
  if (!token) throw new Error("This report does not have a valid paginated PDF export token.");
  const response = await fetch(`${API_BASE}/api/report/pdf/${encodeURIComponent(token)}`, {
    method: "GET",
    headers: { Accept: "application/pdf" },
    cache: "no-store",
  });
  if (!response.ok) {
    const json = await response.json().catch(() => null);
    throw new Error(json?.message || `PDF export failed (HTTP ${response.status}).`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return { blob, filename: match?.[1] || "TVAC-Assessment-Report.pdf" };
}



async function checkScenarioLabAccess(email) {
  const response = await fetch(`${API_BASE}/api/scenario-lab/access/check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: email || localStorage.getItem("tvac_access_email") || null,
    }),
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    throw new Error(formatApiError(json, response.status));
  }

  return json || { allowed: false };
}

async function createScenarioLabCheckout(payload = {}) {
  const response = await fetch(`${API_BASE}/api/stripe/scenario-lab/create-checkout-session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...payload,
      email: payload.email || localStorage.getItem("tvac_access_email") || null,
    }),
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    throw new Error(formatApiError(json, response.status));
  }

  if (!json?.url) {
    throw new Error("Scenario Lab checkout could not be started. Please try again.");
  }

  return json;
}

// Export both named + default
const api = { analyzeCase, localizeReport, downloadReportPdf, checkScenarioLabAccess, createScenarioLabCheckout };

export { analyzeCase, localizeReport, downloadReportPdf, checkScenarioLabAccess, createScenarioLabCheckout };
export default api;
